/**
 * Component Name: HeaderSection
 * Description: A header component for the invoice management module, displaying breadcrumbs, grouping options, and summary statistics.
 * Props:
 *   - breadcrumbItems (array): List of breadcrumb items for navigation.
 *   - handleBreadcrumbHover (function): Callback for handling breadcrumb hover events.
 *   - handleBreadcrumbClick (function): Callback for handling breadcrumb clicks.
 *   - filterState (object): Current filter state for invoices.
 *   - summaryStats (object): Aggregated statistics (total invoices, amount, suppliers, etc.).
 *   - fetchInvoiceData (function): Callback to fetch invoice data.
 *   - handleOpenFilterModal (function): Callback to open the filter modal.
 *   - handleGroupChange (function): Callback to handle group selection changes.
 *   - selectedGroup (string): Current grouping type ('project', 'section', 'po', etc.).
 *   - filteredInvoices (array): List of filtered invoice objects.
 *   - projects (array): List of project objects.
 *   - onChartBarClick (function): Callback for handling chart bar clicks.
 *   - chartDrillDownState (object, optional): State for chart drill-down.
 *   - subTable (object, optional): Sub-table filter data.
 * Feature: Renders a header with interactive breadcrumbs for navigation, a dropdown for selecting grouping options, and a summary stats section with a chart for invoice data visualization in the invoice management interface.
 */
import React from 'react';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import SummaryStats from './summary-section';
import { BREADCRUMB_ITEMS_LABEL, BREADCRUMB_ITEMS_TYPES, INVOICE_GROUPING_OPTIONS, INVOICE_GROUPING_VALUES, INVOICE_UI_LABELS } from '../../util/enums/invoice-management';

const HeaderSection = ({
    breadcrumbItems,
    handleBreadcrumbHover,
    handleBreadcrumbClick,
    filterState,
    summaryStats,
    fetchInvoiceData,
    handleOpenFilterModal,
    handleGroupChange,
    selectedGroup,
    filteredInvoices,
    projects,
    onChartBarClick,
    chartDrillDownState,
    subTable, // Ensure subTable is passed to SummaryStats
}) => {
    // Define grouping options for MultiSelect
    const groupOptions = [
        { label: INVOICE_GROUPING_OPTIONS.MONTH, value: INVOICE_GROUPING_VALUES.MONTH },
        { label: INVOICE_GROUPING_OPTIONS.PROJECT, value: INVOICE_GROUPING_VALUES.PROJECT },
        { label: INVOICE_GROUPING_OPTIONS.PO, value: INVOICE_GROUPING_VALUES.PO },
        { label: INVOICE_GROUPING_OPTIONS.QUARTER, value: INVOICE_GROUPING_VALUES.QUARTER },
        { label: INVOICE_GROUPING_OPTIONS.SECTION, value: INVOICE_GROUPING_VALUES.SECTION },
        { label: INVOICE_GROUPING_OPTIONS.SUPPLIER, value: INVOICE_GROUPING_VALUES.SUPPLIER },
        { label: INVOICE_GROUPING_OPTIONS.NONE, value: INVOICE_GROUPING_VALUES.NONE },
    ];

    // Map selectedGroup string to MultiSelect value
    const selectedGroupOption = groupOptions.find((option) => option.value === selectedGroup) || groupOptions[6]; // Default to 'No Grouping' if not found

    // Handle MultiSelect change
    const handleMultiSelectChange = (selectedOption) => {
        handleGroupChange(selectedOption ? selectedOption.value : INVOICE_GROUPING_VALUES.NONE);
    };

    const getAugmentedBreadcrumbItem = (item, breadcrumbItems) => ({
        ...item,
        projectId:
            item.type === BREADCRUMB_ITEMS_TYPES.STAGE || item.type === BREADCRUMB_ITEMS_TYPES.TASK
                ? breadcrumbItems.find(i => i.type === BREADCRUMB_ITEMS_TYPES.PROJECT)?.id
                : item.id,
        stageId:
            item.type === BREADCRUMB_ITEMS_TYPES.TASK
                ? breadcrumbItems.find(i => i.type === BREADCRUMB_ITEMS_TYPES.STAGE)?.id
                : null,
    });

    return (
        <div className="invoice-management-block">
            <div className="row">
                <div className="w-auto">
                    <h2 className="content-heading text-uppercase">{INVOICE_UI_LABELS.SUPPLIER_INVOICE_MANAGEMENT}</h2>
                </div>
            </div>
            <div className="header content-header">
                <div className="col-12 breadcrumb-block p-0">
                    <Breadcrumb>
                        <Breadcrumb.Item
                            key="all-projects"
                            className="project-hierarchy-trigger"
                            onMouseEnter={() => handleBreadcrumbHover({ type: BREADCRUMB_ITEMS_TYPES.PROJECT, id: null, label: BREADCRUMB_ITEMS_LABEL.PROJECT })}
                            onClick={() => {
                                handleBreadcrumbClick({ type: BREADCRUMB_ITEMS_TYPES.PROJECT, id: null })
                                handleBreadcrumbHover({ type: BREADCRUMB_ITEMS_TYPES.PROJECT, id: null, label: BREADCRUMB_ITEMS_LABEL.PROJECT })
                            }}
                            active={!filterState.selectedProjectId?.value && selectedGroup === INVOICE_GROUPING_VALUES.NONE}
                        >
                            {BREADCRUMB_ITEMS_LABEL.PROJECT}
                        </Breadcrumb.Item>
                        {breadcrumbItems.map((item, index) => {
                            const augmentedItem = getAugmentedBreadcrumbItem(item, breadcrumbItems);
                            return (
                                <Breadcrumb.Item
                                    key={index}
                                    active={item.isActive}
                                    className="project-hierarchy-trigger"
                                    onMouseEnter={() => handleBreadcrumbHover(augmentedItem)}
                                    onFocus={() => handleBreadcrumbHover(augmentedItem)}
                                    onClick={() => handleBreadcrumbClick(item)}
                                >
                                    {item.label}
                                </Breadcrumb.Item>
                            );
                        })}
                    </Breadcrumb>
                </div>

                <SummaryStats
                    summaryStats={summaryStats}
                    fetchInvoiceData={fetchInvoiceData}
                    handleOpenFilterModal={handleOpenFilterModal}
                    handleMultiSelectChange={handleMultiSelectChange}
                    selectedGroupOption={selectedGroupOption}
                    groupOptions={groupOptions}
                    filteredInvoices={filteredInvoices}
                    projects={projects}
                    selectedGroup={selectedGroup}
                    subTable={subTable} // Ensure subTable is passed
                    chartDrillDownState={chartDrillDownState} // Pass chartDrillDownState
                    onChartBarClick={onChartBarClick}
                />
            </div>
        </div>
    );
};

export default HeaderSection;